import {
  createLogger,
  createMetricsLogger,
  CTI,
  enableConsoleOnlyLogging,
  enableLocalHostLogging,
  IMetricsLoggerClient,
} from '@amzn/exs-client-logging/v2';
import {
  IElementsWebComponent,
  IElementsWebComponentOptions,
  PluginDisconnectHandler,
  IElementPlugin,
} from '@amzn/exs-elements-interface';
import { ILogFactory, ILogger } from '@amzn/exs-gem-interface';
import { onCLS, onFCP, onINP, onLCP, onTTFB, onFID, Metric } from 'web-vitals';
import { StandardMetricTypes } from '@amzn/exs-gem-metric-types';
import {
  classifyUserAgent,
  emitLatency,
  getDeviceType,
} from '../../assets/scripts/metrics';
import {
  IS_PROD,
  ROSETTE_GAMMA_CTI,
  ROSETTE_PROD_CTI,
} from '~/src/assets/scripts/constants';

const {
  CumulativeLayoutShift,
  FirstContentfulPaint,
  FirstInputDelay,
  InteractionToNextPaint,
  LargestContentfulPaint,
  TimeToFirstByte,
} = StandardMetricTypes;

interface WebVitalsPluginOptions {
  properties?:
    | { [key: string]: unknown }
    | ((target: IElementsWebComponent) => { [key: string]: unknown });
  dimensions?: Array<Record<string, string>>;
  consoleOnlyLogging?: boolean;
  localHostLogging?: boolean;
}

export function getWebVitalsPlugin(tagName: string) {
  return WebVitalsPlugin(IS_PROD ? ROSETTE_PROD_CTI : ROSETTE_GAMMA_CTI, {
    properties: (element) => {
      const href = new URL(window.location.href);
      const targetId = element.getSetting('targetId');
      return {
        targetId,
        appId: `${tagName}:targetId=${targetId}:${href.host}${href.pathname}`,
      };
    },
    dimensions: [
      {
        ElementName: tagName,
      },
      {
        ElementName: tagName,
        UserAgentType: classifyUserAgent(),
      },
      {
        ElementName: tagName,
        UserAgentType: classifyUserAgent(),
        DeviceType: getDeviceType(),
      },
      {},
    ],
    /* Set both of these options to true to have metrics logged into the browser console */
    consoleOnlyLogging: false,
    localHostLogging: false,
  });
}

export const WebVitalsPlugin = (
  cti: CTI,
  options?: WebVitalsPluginOptions
): IElementPlugin => new Plugin(cti, options);

/**
 * Register web vitals plugin
 */
class Plugin implements IElementPlugin {
  logFactory: ILogFactory;
  logger: ILogger | undefined;
  metricLogger: IMetricsLoggerClient;
  properties: NonNullable<WebVitalsPluginOptions['properties']>;
  dimensions: Array<Record<string, string>>;

  constructor(
    private cti: CTI,
    {
      consoleOnlyLogging,
      localHostLogging,
      properties,
      dimensions,
    }: WebVitalsPluginOptions = {}
  ) {
    if (consoleOnlyLogging) enableConsoleOnlyLogging();
    if (localHostLogging) enableLocalHostLogging();
    this.logFactory = createLogger(this.cti);
    this.metricLogger = createMetricsLogger(this.cti);
    this.properties = properties || {};
    this.dimensions = dimensions || [];
  }

  configure<T extends IElementsWebComponentOptions>(options: T): T {
    this.logger = this.logFactory.create(options.tagName);
    return {
      ...options,
      logger: this.logger,
    };
  }

  onConnected(target: IElementsWebComponent): PluginDisconnectHandler {
    const properties: { [key: string]: unknown } =
      typeof this.properties === 'function'
        ? this.properties(target)
        : this.properties;

    const getMetricLogger = () => {
      const metricLogger = this.metricLogger.new();
      metricLogger.setTimestamp(Date.now());
      Object.entries(properties).forEach(([key, value]) => {
        metricLogger.setProperty(key, value);
      });
      if (this.dimensions.length > 0)
        metricLogger.setDimensions(...this.dimensions);

      return metricLogger;
    };

    const emit = (metricName: StandardMetricTypes) => (metric: Metric) =>
      emitLatency(getMetricLogger(), metricName, metric.value);

    onCLS(emit(CumulativeLayoutShift));
    onFCP(emit(FirstContentfulPaint));
    onFID(emit(FirstInputDelay));
    onINP(emit(InteractionToNextPaint));
    onLCP(emit(LargestContentfulPaint));
    onTTFB(emit(TimeToFirstByte));

    return () => {};
  }
}
